import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart, ArrowRight, ArrowUpRight, Sparkles, HeartHandshake, LifeBuoy, Mail, MapPin, Play } from "lucide-react";
import { useCashApp } from "@/components/CashAppModal";
import Marquee from "@/components/Marquee";
import MediaFrame from "@/components/MediaFrame";
import MediaCard from "@/components/MediaCard";
import ContactForm from "@/components/ContactForm";
import { LogoMark } from "@/components/Logo";
import { GIRL_CODE_LOGO_URL } from "@/components/GirlCodeLogo";
import { SectionHead, Reveal } from "@/components/Reveal";
import { Sparkles as ShimmerSparkles, ShimmerCard } from "@/components/Shimmer";
import { api, mediaUrl } from "@/lib/api";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1619497442016-fb99b15c549d?crop=entropy&cs=srgb&fm=jpg&q=85";
const HERO_IMG =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_youth-center-stl/artifacts/i42wtoo8_56F75A89-482C-493A-8C80-EB9B2F836AD8.png";
const COURT_IMG =
  "https://images.unsplash.com/photo-1786149192020-0a028ef58c88?crop=entropy&cs=srgb&fm=jpg&q=85";

const heroLines = ["This is your space.", "Your voice.", "Your future."];

const CENTER_OFFERINGS = [
  "After-school programming",
  "Tutoring and academic support",
  "Mentorship",
  "Leadership development",
  "Girl Code Project STL",
  "Sports and recreation",
  "Cheer and dance",
  "Life-skills education",
  "Career and entrepreneurship exposure",
  "Safe social and community spaces",
  "Summer programming",
];

const Home = () => {
  const { open } = useCashApp();
  const [media, setMedia] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yMedia = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yArch = useTransform(scrollYProgress, [0, 1], [0, 50]);

  useEffect(() => {
    api
      .get("/media")
      .then((r) => setMedia(r.data))
      .catch(() => setMedia([]));
  }, []);

  const heroVideo = media?.find((m) => m.media_type === "video");
  const highlights = (media || []).slice(0, 3);

  return (
    <main>
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20 bg-gradient-to-b from-lavender-100 via-lavender-50 to-white" data-testid="hero-section">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(124,58,237,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.05) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="absolute top-[-10%] left-[-10%] w-[40rem] h-[40rem] rounded-full bg-purple-200/40 blur-[130px] pointer-events-none" />
        <div className="absolute bottom-[-20%] right-[-5%] w-[36rem] h-[36rem] rounded-full bg-fuchsia-200/30 blur-[130px] pointer-events-none" />
        <motion.svg
          viewBox="0 0 200 200"
          style={{ y: yArch }}
          className="absolute -right-24 bottom-0 h-[75%] opacity-[0.15]"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="archGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#A855F7" />
              <stop offset="1" stopColor="#DDD6FE" />
            </linearGradient>
          </defs>
          <path d="M20 200 C 20 20 180 20 180 200" fill="none" stroke="url(#archGrad)" strokeWidth="12" />
        </motion.svg>

        <div className="relative max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-20 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="eyebrow"
            >
              Y.E.S. · Youth Empowerment &amp; Support · St. Louis
            </motion.p>
            <h1 className="font-display font-extrabold tracking-tight text-ink mt-5 text-[clamp(2.6rem,7vw,5.5rem)] leading-[1.02]">
              {heroLines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === 2 ? "text-shine" : ""}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="text-slate-600 text-base sm:text-lg leading-relaxed mt-6 max-w-xl"
            >
              At <span className="text-purple-800 font-bold">Y.E.S.</span>, young people don&apos;t
              just participate — they discover who they are, what they&apos;re capable of, and where
              they can go next.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.72 }}
              className="text-slate-500 text-base sm:text-lg leading-relaxed mt-4 max-w-xl"
            >
              Through mentorship, leadership, sports, academics, life skills, and real-world
              experiences, we&apos;re creating opportunities for the next generation to grow, lead,
              dream bigger, and be heard.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.78 }}
              className="font-display text-xl sm:text-2xl font-extrabold text-purple-800 mt-6"
              data-testid="hero-belong-line"
            >
              YOU belong here.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="flex flex-wrap items-center gap-3 mt-8"
            >
              <Link to="/programs" data-testid="hero-programs-btn" className="btn-primary px-7 py-3.5">
                Find Your Program <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/partner" data-testid="hero-partner-btn" className="btn-outline px-7 py-3.5">
                Partner With <span className="brand-shine">Y.E.S.</span>
              </Link>
              <button onClick={open} data-testid="hero-support-btn" className="btn-ghost px-5 py-3.5 text-sm">
                <Heart className="w-4 h-4" /> Invest in Our Youth
              </button>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-slate-400 mt-10"
            >
              Youth &amp; Families · Schools · Community Partners · Donors &amp; Foundations
            </motion.p>
          </div>

          <motion.div style={{ y: yMedia }} className="relative">
            <motion.div
              style={{ y: yBadge }}
              className="absolute -top-7 -left-4 sm:-left-10 z-20 glass rounded-2xl px-5 py-4 rotate-[-5deg] animate-floaty shadow-lg shadow-purple-200/60"
              data-testid="hero-badge"
            >
              <div className="flex items-center gap-3">
                <LogoMark className="w-9 h-9" />
                <div className="leading-tight">
                  <p className="font-display font-bold text-slate-900 text-sm">St. Louis, MO</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-purple-700 mt-0.5">For the youth</p>
                </div>
              </div>
            </motion.div>
            {heroVideo ? (
              <video
                controls
                preload="metadata"
                src={mediaUrl(heroVideo.storage_path)}
                className="relative w-full aspect-video object-cover rounded-3xl shadow-2xl shadow-purple-300/50 ring-1 ring-purple-100 z-10"
                data-testid="hero-video"
              />
            ) : (
              <div className="relative z-10" data-testid="hero-photo">
                <img
                  src={HERO_IMG}
                  alt="Young girls building confidence in front of a mirror that reads I Am Enough"
                  className="w-full aspect-[4/3] object-cover rounded-3xl shadow-2xl shadow-purple-300/50 ring-1 ring-purple-100"
                />
                <motion.img
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  src={COURT_IMG}
                  alt="Teens playing basketball on an outdoor court"
                  className="absolute -bottom-10 -left-4 sm:-left-12 w-36 sm:w-48 aspect-[4/3] object-cover rounded-2xl shadow-xl shadow-purple-300/50 ring-4 ring-white z-20 rotate-[-4deg]"
                  data-testid="hero-photo-secondary"
                />
              </div>
            )}
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-16 text-center">
              Real events. Real youth. Published only with media permissions.
            </p>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* ---------- MISSION ---------- */}
      <section className="py-24 sm:py-28" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionHead
            eyebrow="Our Mission"
            title="We're here to open doors."
            sub="Y.E.S. — Youth Empowerment & Support — creates accessible opportunities for young people to learn, lead, build confidence, explore their interests, and prepare for their future."
            center
          />
          <Reveal delay={0.15}>
            <p className="font-display text-xl sm:text-3xl font-bold leading-snug text-purple-950 bg-lavender-100 rounded-3xl px-6 py-10 mt-10 border border-purple-100">
              We believe a family&apos;s finances should never determine how much opportunity a
              child gets to experience.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- MEET Y.E.S. ---------- */}
      <section className="py-24 sm:py-28 border-t border-purple-100" data-testid="meet-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
          <div>
            <SectionHead
              eyebrow="Meet Y.E.S."
              title="It started with one belief:"
              sub="Every young person deserves the chance to become everything they were created to be."
            />
            <Reveal delay={0.1}>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-6">
                Founded by <span className="text-purple-900 font-bold">DaNeisha Green</span> and{" "}
                <span className="text-purple-900 font-bold">Roxanne Torrey</span>, Youth Empowerment
                &amp; Support (Y.E.S.) was built to create more than programs — we&apos;re creating
                access, opportunity, confidence, and a community where young people know they
                belong.
              </p>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-4">
                Together, our founders are turning a shared passion for youth into a growing
                movement where young people are encouraged to dream bigger, find their voice, and
                say <span className="font-display font-extrabold text-purple-800">Y.E.S.</span> to
                their future.
              </p>
              <p className="font-display text-2xl sm:text-3xl font-extrabold text-ink mt-8">
                This is <span className="text-shine">Y.E.S.</span> — and we&apos;re just getting
                started.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="grid gap-5">
              {[
                { name: "DaNeisha Green", role: "Founder", photo: "https://customer-assets-lxgj4vgw.emergentagent.net/job_youth-center-stl/artifacts/inz318ft_CE885E55-E016-4C9E-AF50-B47732DB4317.png", testid: "founder-card-daneisha" },
                { name: "Roxanne Torrey", role: "Co-Founder", photo: "https://customer-assets-lxgj4vgw.emergentagent.net/job_youth-center-stl/artifacts/xbdvam0x_3D0FCA9E-5C50-4B2E-B306-5667ABCCBB84.png", testid: "founder-card-roxanne" },
              ].map((f) => (
                <div
                  key={f.name}
                  className="flex items-center gap-5 rounded-3xl bg-lavender-50 border border-purple-100 p-6 sm:p-7 shadow-sm hover:shadow-lg hover:shadow-purple-200/50 transition-shadow"
                  data-testid={f.testid}
                >
                  <img
                    src={f.photo}
                    alt={`${f.name}, ${f.role} of Y.E.S.`}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover shrink-0 shadow-lg shadow-purple-300/50 ring-2 ring-purple-300"
                  />
                  <div>
                    <p className="font-display text-xl font-extrabold text-slate-900">{f.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-purple-700 mt-1">{f.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- PROGRAMS BENTO ---------- */}
      <section className="py-24 sm:py-28 bg-lavender-50 border-y border-purple-100" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHead
            eyebrow="Programs"
            title="There's more than one way to discover who you're meant to be."
          />
          <Reveal delay={0.1}>
            <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-purple-700 mt-6">
              Sports. Sisterhood. Leadership. Money. Confidence. School. Life.
            </p>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-4 max-w-2xl">
              Y.E.S. gives young people opportunities to explore it all — while surrounded by adults
              who believe in them.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {/* BDT Cheer */}
            <ShimmerCard className="rounded-2xl">
              <div className="relative h-full">
                <ShimmerSparkles count={8} />
                <div className="h-full rounded-2xl bg-white border border-purple-100 p-7 flex flex-col transition-transform duration-300 hover:-translate-y-1 shadow-sm">
                  <div className="w-11 h-11 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5 text-purple-700" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-slate-900 mt-5">BDT Cheer</h3>
                  <p className="text-fuchsia-600 text-sm font-bold mt-2">Bring the energy. Build the confidence.</p>
                  <p className="text-slate-500 text-sm leading-relaxed mt-2">
                    More than sidelines and performances. Cheer develops teamwork, discipline,
                    confidence, commitment, leadership, and community.
                  </p>
                </div>
              </div>
            </ShimmerCard>

            {/* Girl Code — wide */}
            <ShimmerCard className="rounded-2xl md:col-span-2">
              <div className="relative h-full">
                <ShimmerSparkles count={8} />
                <div className="h-full rounded-2xl bg-white border border-purple-100 p-7 flex flex-col sm:flex-row gap-6 transition-transform duration-300 hover:-translate-y-1 shadow-sm">
                  <div className="shrink-0">
                    <div className="bg-lavender-50 ring-1 ring-purple-200 rounded-xl p-1.5 w-24 shadow-lg shadow-purple-200/70 -rotate-3">
                      <img src={GIRL_CODE_LOGO_URL} alt="Girl Code Project STL logo" className="w-full aspect-square object-contain" data-testid="girl-code-logo-tile" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-slate-900">Girl Code Project STL</h3>
                    <p className="text-fuchsia-600 text-sm font-bold mt-2">Real conversations. Real sisterhood. Real growth.</p>
                    <p className="text-slate-500 text-sm leading-relaxed mt-2">
                      A space for girls ages 10–17 to talk about the things that actually matter —
                      confidence, friendships, money, goals, leadership, entrepreneurship, self-love,
                      and becoming the young woman YOU want to be.
                    </p>
                  </div>
                </div>
              </div>
            </ShimmerCard>

            {/* Leadership & Mentorship */}
            <Reveal className="h-full">
              <div className="h-full rounded-2xl bg-white border border-purple-100 p-7 flex flex-col transition-transform duration-300 hover:-translate-y-1 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center shrink-0">
                  <HeartHandshake className="w-5 h-5 text-purple-700" />
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mt-5">Leadership &amp; Mentorship</h3>
                <p className="text-purple-700 text-sm font-bold mt-2">Your voice matters. Learn how to use it.</p>
                <p className="text-slate-500 text-sm leading-relaxed mt-2">
                  Mentors, workshops, and experiences help young people communicate confidently, set
                  goals, solve problems, advocate for themselves, and lead others.
                </p>
              </div>
            </Reveal>

            {/* Life Skills */}
            <Reveal delay={0.05} className="h-full">
              <div className="h-full rounded-2xl bg-white border border-purple-100 p-7 flex flex-col transition-transform duration-300 hover:-translate-y-1 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center shrink-0">
                  <LifeBuoy className="w-5 h-5 text-purple-700" />
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mt-5">Life Skills</h3>
                <p className="text-purple-700 text-sm font-bold mt-2">Because there&apos;s a lot about life nobody teaches you in class.</p>
                <p className="text-slate-500 text-sm leading-relaxed mt-2">
                  Financial literacy, credit, digital safety, self-care, etiquette, career
                  preparation, healthy relationships, communication — skills that carry into
                  adulthood.
                </p>
              </div>
            </Reveal>

            {/* Flexible growth tile */}
            <Reveal delay={0.1} className="h-full">
              <Link
                to="/programs"
                data-testid="programs-cta-tile"
                className="h-full rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 p-7 flex flex-col justify-between group shadow-lg shadow-purple-300/40"
              >
                <p className="font-display text-2xl font-extrabold text-white leading-snug">
                  And we&apos;re just getting started
                </p>
                <span className="inline-flex items-center gap-2 text-white font-semibold text-sm mt-6">
                  See All Programs <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- EVENT HIGHLIGHTS ---------- */}
      <section className="py-24 sm:py-28" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <SectionHead
              eyebrow="From our events"
              title="Event highlights"
              sub="Fresh photos and clips from real Y.E.S. moments — added by our team as they happen."
            />
            <Reveal delay={0.1}>
              <Link to="/media" data-testid="highlights-view-all-btn" className="btn-outline px-6 py-3 text-sm shrink-0">
                View Events &amp; Media <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {media === null
              ? [1, 2, 3].map((i) => <div key={i} className="aspect-video rounded-2xl bg-purple-100 animate-pulse" />)
              : highlights.length
                ? highlights.map((p, i) => <MediaCard key={p.id} post={p} index={i} />)
                : [1, 2, 3].map((i) => (
                    <MediaFrame
                      key={i}
                      label={`Event Highlight Slot ${i}`}
                      sub="Photos and clips from real Y.E.S. events will appear here once uploaded and approved."
                    />
                  ))}
          </div>
        </div>
      </section>

      {/* ---------- DONATION ---------- */}
      <section className="py-24 sm:py-28" data-testid="donation-section">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal>
            <div className="relative rounded-3xl bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 p-8 sm:p-14 overflow-hidden shadow-2xl shadow-purple-300/50">
              <div className="absolute -top-24 right-0 w-96 h-96 rounded-full bg-white/15 blur-[100px] pointer-events-none" />
              <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-purple-100">Create Access</p>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold text-white tracking-tight mt-4 leading-tight">
                You&apos;re not just making a donation.
                <br />
                You&apos;re creating access.
              </h2>
              <div className="grid lg:grid-cols-2 gap-8 mt-8">
                <p className="text-purple-50 leading-relaxed">
                  Somewhere in our community is a young person with talent, ideas, leadership
                  potential, and a future bigger than their circumstances.
                  <br />
                  <span className="text-white font-bold">Sometimes all that&apos;s missing is access.</span>
                </p>
                <p className="text-purple-100 leading-relaxed">
                  Support for Y.E.S. helps provide mentorship, academic support, sports, leadership
                  development, transportation, supplies, enrichment experiences, workshops, meals,
                  and opportunities families may not otherwise be able to afford.
                </p>
              </div>
              <p className="text-white font-semibold mt-8">
                Help us make sure opportunity isn&apos;t reserved for the families who can afford it.
              </p>
              <div className="flex flex-wrap gap-3 mt-9">
                <button onClick={open} data-testid="donate-invest-btn" className="btn bg-white text-purple-800 hover:bg-purple-50 shadow-lg px-7 py-3.5">
                  <Heart className="w-4 h-4" /> Invest in Our Youth
                </button>
                <Link to="/#contact" data-testid="donate-monthly-btn" className="btn border border-white/70 text-white hover:bg-white/10 px-7 py-3.5">
                  Become a Monthly Supporter
                </Link>
                <Link to="/#contact" data-testid="donate-sponsor-btn" className="btn border border-white/70 text-white hover:bg-white/10 px-7 py-3.5">
                  Sponsor a Program
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- WHERE WE'RE GOING ---------- */}
      <section className="py-24 sm:py-28 bg-lavender-50 border-y border-purple-100" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-purple-100 shadow-xl shadow-purple-200/50 aspect-[4/3]">
              <img src={ARCH_IMG} alt="St. Louis skyline and Gateway Arch at dusk" loading="lazy" className="w-full h-full object-cover" />
              <span className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-purple-900 border border-purple-200" data-testid="vision-chip">
                The Vision · Not Yet Open
              </span>
            </div>
          </Reveal>
          <div>
            <SectionHead
              eyebrow="Where We're Going"
              title="A youth center built around what young people need"
              sub="Y.E.S. is working toward a dedicated youth-centered space serving North St. Louis County — one address where young people can access:"
            />
            <Reveal delay={0.15}>
              <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {CENTER_OFFERINGS.map((item) => (
                  <p key={item} className="flex items-start gap-2.5 text-slate-700 text-sm">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                    {item}
                  </p>
                ))}
              </div>
              <p className="text-slate-600 leading-relaxed mt-8">
                This is a vision in progress — and the schools, businesses, foundations,
                municipalities, and community organizations who join now become{" "}
                <span className="text-purple-900 font-bold">founding partners</span> in what&apos;s next.
              </p>
              <Link to="/#contact" data-testid="vision-build-btn" className="btn-primary px-7 py-3.5 mt-8">
                Help Build What&apos;s Next <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- CONTACT ---------- */}
      <section id="contact" className="py-24 sm:py-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-14">
          <div>
            <SectionHead
              eyebrow="Contact"
              title="Let's talk"
              sub="Whether you're a young person, a parent, or a potential partner — send a message. It goes straight to our team."
            />
            <Reveal delay={0.15}>
              <div className="mt-9 space-y-4">
                <a
                  href="mailto:daneisha.green1@yahoo.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-3 text-slate-700 hover:text-purple-800 transition-colors"
                >
                  <span className="w-10 h-10 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-purple-700" />
                  </span>
                  daneisha.green1@yahoo.com
                </a>
                <p className="flex items-center gap-3 text-slate-500 text-sm">
                  <span className="w-10 h-10 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-purple-700" />
                  </span>
                  St. Louis, Missouri
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;